import { useState } from "react";
import { View, Text, ScrollView, Platform } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Pill, Stethoscope, Snowflake, Plus } from "lucide-react-native";
import { EMBER } from "../../../onboarding/theme";
import type { CostsStackParamList } from "../../types";
import { useRegion, useTranslations } from "@src/packages/i18n";
import RegionGatePlaceholder from "../../components/RegionGatePlaceholder";
import {
  BackLink,
  CardSurface,
  CardTitleText,
  MetricLabelText,
  MetricValueText,
  EmptyState,
  DialogIcon,
  CompactActionButton,
  ActionBanner,
} from "@louise/moon-ui";
import { useCoverage } from "../../../../../packages/coverage/hooks/useCoverage";
import { useCoverageFinancials } from "../../../../../packages/coverage/hooks/useCoverageFinancials";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import { SCREEN_PADDING_TOP } from "@louise/moon-ui/theme/tokens";
import InsuranceInfoModal from "../YouScreen/modals/InsuranceInfoModal";
import InsuranceCard from "../YouScreen/sections/InsuranceCard";
import { useSmartBack } from "../../hooks/useSmartBack";
import insuranceEmptyIllustration from "../../../assets/images/you-icons/coverage-benefits-row-3d.png";
import { SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE } from "../../components/ScreenTitle";
import { colors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<CostsStackParamList, "Insurance">;

function CoverageIndicator({
  icon: Icon,
  label,
  covered,
}: {
  icon: typeof Pill;
  label: string;
  covered: boolean;
}) {
  return (
    <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
      <Icon
        size={14}
        color={covered ? EMBER.colors.green : EMBER.colors.textFaint}
        strokeWidth={1.5}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: covered ? EMBER.colors.text : EMBER.colors.textFaint,
        }}
      >
        {label}: {covered ? "Covered" : "Not covered"}
      </Text>
    </View>
  );
}

export default function InsuranceScreen({ route }: Props) {
  const t = useTranslations();
  const region = useRegion();
  const {
    primaryPolicy,
    hasInsurance,
    verificationStatus,
    fertilityCoverage,
    isLoading,
  } = useCoverage();
  const financials = useCoverageFinancials();
  const [modalVisible, setModalVisible] = useState(false);
  const handleBack = useSmartBack({
    returnTab: route.params?.returnTab,
    fallbackTab: "Costs",
    fallbackScreen: "CostsOverview",
  });

  const carrierName =
    primaryPolicy?.payer?.displayName ??
    primaryPolicy?.payer?.name ??
    primaryPolicy?.insuranceCarrier ??
    null;

  if (region !== "US") return <RegionGatePlaceholder />;

  return (
    <GlassScreenLayout
      overlay={
        <InsuranceInfoModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          primaryPolicy={primaryPolicy}
          hasInsurance={hasInsurance}
          verificationStatus={verificationStatus}
          fertilityCoverage={fertilityCoverage}
          isLoading={isLoading}
        />
      }
    >
      <ScrollView
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingTop: SCREEN_PADDING_TOP,
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* Back */}
        <BackLink
          label={route.params?.returnTab ? t("you.shared.back") : "Costs"}
          onPress={handleBack}
          style={{ marginBottom: 8 }}
        />

        {/* Title */}
        <Text
          className="font-dm-sans"
          style={
            {
              fontSize: 38,
              fontWeight: "700",
              color: colors.deepBrown,
              lineHeight: 42,
              letterSpacing: -0.5,
              ...(Platform.OS === "web" && {
                ...SCREEN_TITLE_GRADIENT_BACKGROUND_STYLE,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }),
            } as Record<string, unknown>
          }
        >
          Insurance
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            color: EMBER.colors.textLight,
            marginTop: 2,
            marginBottom: 20,
          }}
        >
          Your coverage details
        </Text>

        {/* Content */}
        {/* The textured card Profile uses, so Costs and Profile show the same
            record (#3812); it also carries its own loading state. With nothing
            on file, the illustrated empty state from the empty-state review
            (#3823) asks for the first policy instead. */}
        {isLoading || hasInsurance ? (
          <InsuranceCard
            insuranceInfo={{
              provider: carrierName,
              planName: primaryPolicy?.planName ?? null,
              memberId: primaryPolicy?.memberId ?? null,
            }}
            hasInsurance={hasInsurance}
            verificationStatus={verificationStatus}
            loading={isLoading}
            bottomSpacing={16}
            onAdd={() => setModalVisible(true)}
            onEdit={() => setModalVisible(true)}
          />
        ) : (
          <EmptyState
            testID="insurance-empty-state"
            density="compact"
            illustration={
              <DialogIcon
                source={insuranceEmptyIllustration}
                width={68}
                height={80}
                testID="insurance-empty-illustration"
              />
            }
            title={t("costs.insurance_screen.no_insurance_on_file")}
            titleVariant="featured"
            subtitle={t("costs.insurance_screen.add_insurance_to_see_coverage")}
            subtitleStyle={{ maxWidth: 280 }}
            action={
              <CompactActionButton
                label={t("costs.insurance_screen.add_insurance")}
                action="costs.insurance_screen.add_insurance"
                variant="primary"
                leadingIcon={Plus}
                onPress={() => setModalVisible(true)}
              />
            }
            style={{ paddingVertical: 28 }}
          />
        )}

        {!isLoading && hasInsurance ? (
          <View style={{ gap: 16 }}>
            {/* Coverage Financials Card */}
            {verificationStatus === "verified" &&
              fertilityCoverage?.hasCoverage && (
                <CardSurface style={{ padding: 20 }}>
                  <CardTitleText style={{ marginBottom: 16 }}>
                    {t("costs.insurance_screen.coverage_details")}
                  </CardTitleText>

                  {/* Financials Grid */}
                  <View
                    style={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      gap: 14,
                      marginBottom: 16,
                    }}
                  >
                    {financials.deductible && (
                      <View style={{ minWidth: "45%" }}>
                        <MetricLabelText>Deductible</MetricLabelText>
                        <MetricValueText style={{ marginTop: 4 }}>
                          {financials.deductible}
                        </MetricValueText>
                      </View>
                    )}
                    {financials.outOfPocketMax && (
                      <View style={{ minWidth: "45%" }}>
                        <MetricLabelText>
                          {t("costs.insurance_screen.oop_max")}
                        </MetricLabelText>
                        <MetricValueText style={{ marginTop: 4 }}>
                          {financials.outOfPocketMax}
                        </MetricValueText>
                      </View>
                    )}
                    {financials.coinsurancePercent && (
                      <View style={{ minWidth: "45%" }}>
                        <MetricLabelText>Coinsurance</MetricLabelText>
                        <MetricValueText style={{ marginTop: 4 }}>
                          {financials.coinsurancePercent}
                        </MetricValueText>
                      </View>
                    )}
                    {financials.treatmentsCovered.length > 0 && (
                      <View style={{ minWidth: "45%" }}>
                        <MetricLabelText>Treatments</MetricLabelText>
                        <MetricValueText
                          style={{ color: EMBER.colors.green, marginTop: 4 }}
                        >
                          {financials.treatmentsCovered.length} covered
                        </MetricValueText>
                      </View>
                    )}
                  </View>

                  {/* Coverage Indicators */}
                  <View style={{ gap: 8 }}>
                    {financials.medsCovered != null && (
                      <CoverageIndicator
                        icon={Pill}
                        label="Medications"
                        covered={financials.medsCovered}
                      />
                    )}
                    {financials.diagnosticsCovered != null && (
                      <CoverageIndicator
                        icon={Stethoscope}
                        label="Diagnostics"
                        covered={financials.diagnosticsCovered}
                      />
                    )}
                    {financials.cryostorageCovered != null && (
                      <CoverageIndicator
                        icon={Snowflake}
                        label="Cryostorage"
                        covered={financials.cryostorageCovered}
                      />
                    )}
                  </View>

                  {/* Treatment Limits */}
                  {financials.treatmentLimits.length > 0 && (
                    <View style={{ marginTop: 12 }}>
                      {financials.treatmentLimits.map((limit, i) => (
                        <Text
                          key={i}
                          className="font-dm-sans"
                          style={{
                            fontSize: 12,
                            color: EMBER.colors.textLight,
                            lineHeight: 18,
                          }}
                        >
                          {limit}
                        </Text>
                      ))}
                    </View>
                  )}
                </CardSurface>
              )}

            {/* Pending / Unverified Status */}
            {verificationStatus === "pending" && (
              <CardSurface style={{ padding: 20 }}>
                <ActionBanner
                  variant="info"
                  message={t("costs.insurance_screen.verifying_coverage")}
                />
              </CardSurface>
            )}

            {verificationStatus === "unverified" && (
              <CardSurface style={{ padding: 20 }}>
                <ActionBanner
                  variant="info"
                  message={t(
                    "costs.insurance_screen.submit_to_verify_coverage",
                  )}
                />
              </CardSurface>
            )}
          </View>
        ) : null}
      </ScrollView>
    </GlassScreenLayout>
  );
}
